import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import TaskModal from '../components/TaskModal';

/**
 * Tasks tab — shows open tasks for the matter.
 * Uses TaskModal for creating and editing tasks.
 */
export default function TasksTab() {
    const { linkedMatter, userEmail } = useAddin();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterStatus, setFilterStatus] = useState('active');

    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);

    // Fetch active tasks
    const fetchTasks = async () => {
        const matterId = linkedMatter?.id || linkedMatter?.matter_id;
        if (!matterId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await webhook.matterTasks({
                userEmail,
                matterId: matterId,
                taskAction: 'fetch'
            });

            // Handle different possible response structures
            let tasksData = [];
            if (Array.isArray(res)) tasksData = res;
            else if (res.data) {
                if (Array.isArray(res.data)) tasksData = res.data;
                else if (res.data.tasks && Array.isArray(res.data.tasks)) tasksData = res.data.tasks;
            } else if (res.tasks && Array.isArray(res.tasks)) {
                tasksData = res.tasks;
            }

            setTasks(tasksData);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch tasks:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, [linkedMatter?.id, linkedMatter?.matter_id, userEmail]);

    const handleCreateTaskClick = () => {
        setEditingTask(null);
        setIsTaskModalOpen(true);
    };

    const handleStartEdit = (task) => {
        setEditingTask(task);
        setIsTaskModalOpen(true);
    };

    const handleDelete = async (task) => {
        if (!window.confirm(`Are you sure you want to delete "${task.name}"?`)) return;
        try {
            await webhook.matterTasks({
                userEmail,
                matterId: linkedMatter.id || linkedMatter.matter_id,
                taskAction: 'delete',
                taskId: task.id,
                etag: task.etag
            });
            await fetchTasks();
        } catch (err) {
            alert('Failed to delete task: ' + err.message);
        }
    };

    const handleQuickStatusChange = async (task, newStatus) => {
        try {
            await webhook.matterTasks({
                userEmail,
                matterId: linkedMatter.id || linkedMatter.matter_id,
                taskAction: 'update',
                taskId: task.id,
                etag: task.etag,
                taskData: {
                    name: task.name,
                    description: task.description || '',
                    status: newStatus
                } // Simple update
            });
            await fetchTasks();
        } catch (err) {
            alert('Failed to update status: ' + err.message);
        }
    };

    if (!linkedMatter) {
        return (
            <div className="addin-body" style={{ padding: '20px', textAlign: 'center', color: '#8a8a8a' }}>
                <p>No matter linked.</p>
            </div>
        );
    }

    return (
        <div className="addin-body">
            <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>Tasks</div>
                <button
                    onClick={handleCreateTaskClick}
                    className="confirm-btn teal"
                    style={{ padding: '4px 12px', margin: 0, width: 'auto', fontSize: '11px' }}
                >
                    + New Task
                </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'nowrap', marginTop: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
                <button onClick={() => setFilterStatus('all')} className={filterStatus === 'all' ? 'confirm-btn' : ''} style={{ flexShrink: 0, padding: '4px 12px', width: 'auto', margin: 0, background: filterStatus === 'all' ? '' : 'transparent', border: '1px solid var(--border-color)', color: filterStatus === 'all' ? '' : 'var(--text-color)', cursor: 'pointer', borderRadius: '4px', fontSize: '11px' }}>All</button>
                <button onClick={() => setFilterStatus('active')} className={filterStatus === 'active' ? 'confirm-btn' : ''} style={{ flexShrink: 0, padding: '4px 12px', width: 'auto', margin: 0, background: filterStatus === 'active' ? '' : 'transparent', border: '1px solid var(--border-color)', color: filterStatus === 'active' ? '' : 'var(--text-color)', cursor: 'pointer', borderRadius: '4px', fontSize: '11px' }}>Pending / In Progress</button>
                <button onClick={() => setFilterStatus('completed')} className={filterStatus === 'completed' ? 'confirm-btn' : ''} style={{ flexShrink: 0, padding: '4px 12px', width: 'auto', margin: 0, background: filterStatus === 'completed' ? '' : 'transparent', border: '1px solid var(--border-color)', color: filterStatus === 'completed' ? '' : 'var(--text-color)', cursor: 'pointer', borderRadius: '4px', fontSize: '11px' }}>Completed</button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a', fontSize: '12px' }}>Loading tasks...</div>
            ) : error ? (
                <div style={{ color: 'var(--orange)', padding: '10px', fontSize: '12px' }}>Error: {error}</div>
            ) : tasks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a', fontSize: '12px' }}>No tasks found.</div>
            ) : (() => {
                const filteredTasks = tasks.filter(t => {
                    if (filterStatus === 'all') return true;
                    if (filterStatus === 'active') return t.status === 'pending' || t.status === 'in_progress';
                    if (filterStatus === 'completed') return t.status === 'completed';
                    return true;
                });

                if (filteredTasks.length === 0) {
                    const statusLabel = filterStatus === 'active' ? 'active' : filterStatus;
                    return <div style={{ textAlign: 'center', padding: '20px', color: '#8a8a8a', fontSize: '12px' }}>No {statusLabel} tasks found.</div>;
                }

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {filteredTasks.map(task => {
                            return (
                                <div key={task.id} className="info-block" style={{ padding: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="checkbox"
                                                checked={task.status === 'completed'}
                                                onChange={(e) => handleQuickStatusChange(task, e.target.checked ? 'completed' : 'pending')}
                                                style={{ cursor: 'pointer', accentColor: 'var(--teal)' }}
                                            />
                                            <div style={{ fontWeight: 600, color: task.status === 'completed' ? '#8a8a8a' : 'var(--text-color)', textDecoration: task.status === 'completed' ? 'line-through' : 'none', fontSize: '13px' }}>
                                                {task.name}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <span onClick={() => handleStartEdit(task)} style={{ cursor: 'pointer', fontSize: '11px', color: 'var(--teal)' }}>Edit</span>
                                            <span onClick={() => handleDelete(task)} style={{ cursor: 'pointer', fontSize: '11px', color: 'var(--orange)' }}>Delete</span>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '11px', color: '#8a8a8a', paddingLeft: '22px' }}>
                                        {task.assignee?.name && (
                                            <span style={{ color: 'var(--text-color)' }}>
                                                Assignee: {task.assignee.name}
                                            </span>
                                        )}
                                        {task.due_at && (
                                            <span style={{ color: (new Date(task.due_at) < new Date() && task.status !== 'completed') ? 'var(--orange)' : 'inherit' }}>
                                                Due: {new Date(task.due_at).toLocaleDateString()}
                                            </span>
                                        )}
                                        <span style={{ textTransform: 'capitalize' }}>
                                            {task.status.replace('_', ' ')}
                                        </span>
                                        {task.priority !== 'Normal' && (
                                            <span style={{ color: task.priority === 'High' ? 'var(--orange)' : 'inherit' }}>
                                                {task.priority}
                                            </span>
                                        )}
                                    </div>
                                    {task.description && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#b0b0b0', paddingLeft: '22px', whiteSpace: 'pre-wrap' }}>
                                            {task.description}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            {isTaskModalOpen && (
                <TaskModal
                    isOpen={isTaskModalOpen}
                    onClose={() => setIsTaskModalOpen(false)}
                    initialTask={editingTask}
                    onSuccess={fetchTasks}
                />
            )}
        </div>
    );
}
